import { WebDashboardPlanLimitSource, WebDashboardPlanLimitStatus } from "@coeff/api";
import { DatePicker, Descriptions, InputNumber, message, Modal, Space, Tag } from "antd";
import moment from "moment";
import React, { useCallback, useState } from "react";
import styled from "styled-components";

import { DEPRECATED_Button, Input } from "../../components";
import { COLORS } from "../../constants";
import { useApiContext } from "../../utils";

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 800;
  margin: 0 0 4px 0;
`;

const Subtitle = styled.p`
  color: ${COLORS.black45};
  margin: 0 0 20px 0;
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 20px;
`;

const Dash = () => <span style={{ color: COLORS.black45 }}>—</span>;

const fmtDate = (dt?: string) => (dt ? new Date(dt).toLocaleString() : undefined);

// Expiry is a UTC end-of-day instant, so render just the UTC calendar date.
const fmtUtcDate = (dt?: string) => (dt ? moment.utc(dt).format("ll") : undefined);

// Snap a picked calendar date to end-of-day UTC.
const toUtcEndOfDay = (date: moment.Moment) =>
  moment.utc(date.format("YYYY-MM-DD")).endOf("day").toDate();

export const WebDashboardPlanLimit: React.FC = () => {
  const { apiClient } = useApiContext();

  const [domainInput, setDomainInput] = useState("");
  const [loadedDomain, setLoadedDomain] = useState("");
  const [status, setStatus] = useState<WebDashboardPlanLimitStatus | null>(null);
  const [loading, setLoading] = useState(false);

  // Set-override modal state.
  const [setModalOpen, setSetModalOpen] = useState(false);
  const [count, setCount] = useState<number | null>(null);
  const [note, setNote] = useState("");
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [saving, setSaving] = useState(false);

  const search = useCallback(async () => {
    const trimmed = domainInput.trim().toLowerCase();
    if (!trimmed) {
      return;
    }
    setLoading(true);
    try {
      const res = await apiClient.getWebDashboardPlanLimit({ domain: trimmed });
      setStatus(res.data.status);
      setLoadedDomain(trimmed);
    } catch {
      message.error("Failed to load plan limit — is the domain valid?");
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [apiClient, domainInput]);

  const openSetModal = () => {
    // Prefill from the current override so an edit starts from today's values.
    setCount(status?.override?.count ?? status?.effective_limit ?? null);
    setNote("");
    setExpiresAt(status?.override?.expires_at ? new Date(status.override.expires_at) : null);
    setSetModalOpen(true);
  };

  const handleSave = async () => {
    if (count === null || count < 0 || note.trim() === "") {
      return;
    }
    setSaving(true);
    try {
      const res = await apiClient.setWebDashboardPlanLimitOverride({
        domain: loadedDomain,
        set_web_dashboard_plan_limit_override_request: {
          count,
          note: note.trim(),
          expires_at: expiresAt ? expiresAt.toISOString() : undefined,
        },
      });
      setStatus(res.data.status);
      setSetModalOpen(false);
      message.success("Override saved");
    } catch {
      message.error("Failed to save override");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = () => {
    Modal.confirm({
      title: "Remove override?",
      content: `The limit for ${loadedDomain} will fall back to the Chargebee-synced value.`,
      okText: "Remove",
      okButtonProps: { danger: true },
      onOk: async () => {
        const res = await apiClient.removeWebDashboardPlanLimitOverride({ domain: loadedDomain });
        setStatus(res.data.status);
        message.success("Override removed");
      },
    });
  };

  const override = status?.override;
  const overrideExpired = !!override && !status?.override_active;

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", padding: 24 }}>
      <PageTitle>Dashboard Plan Limit</PageTitle>
      <Subtitle>
        View and override a domain family's web dashboard limit. The override is preserved across
        Chargebee syncs; the effective limit is the larger of the synced limit and the override.
      </Subtitle>

      <SearchBar>
        <Input
          style={{ width: 320 }}
          placeholder="company.com"
          value={domainInput}
          onChange={e => setDomainInput(e.target.value)}
          onPressEnter={search}
          allowClear
        />
        <DEPRECATED_Button type="primary" onClick={search} loading={loading}>
          Search
        </DEPRECATED_Button>
      </SearchBar>

      {status && (
        <div style={{ maxWidth: 720 }}>
          <Descriptions bordered column={1} size="small" style={{ marginBottom: 20 }}>
            <Descriptions.Item label="Domain">{status.domain}</Descriptions.Item>
            <Descriptions.Item label="Effective limit">
              {status.effective_limit ?? <Dash />}
              {status.effective_source && (
                <Tag
                  style={{ marginLeft: 8 }}
                  color={
                    status.effective_source === WebDashboardPlanLimitSource.ManualOverride
                      ? "blue"
                      : "default"
                  }
                >
                  {status.effective_source === WebDashboardPlanLimitSource.ManualOverride
                    ? "Manual override"
                    : "Subscription sync"}
                </Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Active dashboards">
              {status.active_dashboard_count}
            </Descriptions.Item>
            <Descriptions.Item label="Chargebee-synced limit">
              {status.synced_count ?? <Dash />}
            </Descriptions.Item>
            <Descriptions.Item label="Manual override">
              {override ? (
                <Space direction="vertical" size={2}>
                  <span>
                    <strong>{override.count}</strong>
                    {overrideExpired && (
                      <Tag color="red" style={{ marginLeft: 8 }}>
                        Expired
                      </Tag>
                    )}
                  </span>
                  {override.note && (
                    <span style={{ color: COLORS.black45 }}>Note: {override.note}</span>
                  )}
                  <span style={{ color: COLORS.black45, fontSize: 12 }}>
                    {override.set_by_email && `Set by ${override.set_by_email}`}
                    {fmtDate(override.set_at) && ` on ${fmtDate(override.set_at)}`}
                    {override.expires_at
                      ? ` · Expires ${fmtUtcDate(override.expires_at)}`
                      : " · Never expires"}
                  </span>
                </Space>
              ) : (
                <Dash />
              )}
            </Descriptions.Item>
          </Descriptions>

          <Space>
            <DEPRECATED_Button type="primary" onClick={openSetModal}>
              {override ? "Edit override" : "Set override"}
            </DEPRECATED_Button>
            {override && (
              <DEPRECATED_Button danger onClick={handleRemove}>
                Remove override
              </DEPRECATED_Button>
            )}
          </Space>
        </div>
      )}

      {!status && loadedDomain && !loading && (
        <span style={{ color: COLORS.black45 }}>No data for {loadedDomain}</span>
      )}

      <Modal
        title={`${override ? "Edit" : "Set"} override for ${loadedDomain}`}
        open={setModalOpen}
        onCancel={() => setSetModalOpen(false)}
        okText="Save"
        okButtonProps={{
          disabled: count === null || count < 0 || note.trim() === "",
          loading: saving,
        }}
        onOk={handleSave}
      >
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <div>
            <div style={{ marginBottom: 4 }}>Dashboard limit</div>
            <InputNumber
              min={0}
              style={{ width: "100%" }}
              value={count ?? undefined}
              onChange={value => setCount(typeof value === "number" ? value : null)}
            />
          </div>
          <div>
            <div style={{ marginBottom: 4 }}>Expires (optional — blank = never)</div>
            <DatePicker
              style={{ width: "100%" }}
              value={expiresAt ? moment.utc(expiresAt) : null}
              disabledDate={current => current && current < moment().startOf("day")}
              onChange={date => setExpiresAt(date ? toUtcEndOfDay(date) : null)}
            />
          </div>
          <div>
            <div style={{ marginBottom: 4 }}>Reason (required)</div>
            <Input.TextArea
              placeholder="Why is this override being set?"
              value={note}
              onChange={e => setNote(e.target.value)}
              rows={3}
            />
          </div>
        </Space>
      </Modal>
    </div>
  );
};
